/**
 * Console toasts (QA iteration 2 UX-211).
 *
 * A destructive action that *can* be walked back says so right after it happens, with the walk-back
 * one keyboard stop away: unpublishing a song shows "Se despublicó «X»" with an **Deshacer** action
 * that republishes it. The window is `UNDO_MS` (8 s) — long enough to notice the mistake, short
 * enough that the toast is not console furniture.
 *
 * The region is `aria-live="polite"`: a screen reader hears the sentence, and the action button is
 * a normal tab stop, so undo is reachable without a mouse.
 */
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { cn } from '../../lib/cn';
import { Button } from './button';

/** How long an undoable toast stays up (docs/06 §1.4 — "republishes within ~8 s"). */
export const UNDO_MS = 8000;
const DEFAULT_MS = 4000;

export interface ToastRequest {
  message: string;
  tone?: 'ok' | 'danger';
  /** Optional walk-back. Its presence stretches the default lifetime to `UNDO_MS`. */
  action?: { label: string; onAction: () => void };
  durationMs?: number;
}

interface ToastState extends ToastRequest {
  id: number;
}

const ToastContext = createContext<((request: ToastRequest) => void) | null>(null);

/** `toast({ message, action })`. A no-op outside a `ToastProvider`. */
export function useToast(): (request: ToastRequest) => void {
  return useContext(ToastContext) ?? (() => {});
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastState[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((toast) => toast.id !== id));
  }, []);

  // One toast at a time: a second action replaces the first, so the "Deshacer" on screen always
  // belongs to the last thing the operator did.
  const push = useCallback((request: ToastRequest) => {
    setToasts([{ ...request, id: (nextId += 1) }]);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      {/* A live region, not `role="status"`: the screens already own that role for their own
          inline confirmations, and a second one would be ambiguous to both a reader and a test. */}
      <div
        aria-live="polite"
        aria-atomic="false"
        data-testid="toasts"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((toast) => (
          <ToastRow key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastRow({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }) {
  const life = toast.durationMs ?? (toast.action ? UNDO_MS : DEFAULT_MS);

  useEffect(() => {
    const timer = setTimeout(onDismiss, life);
    return () => clearTimeout(timer);
  }, [life, onDismiss]);

  return (
    <div
      data-testid="toast"
      className={cn(
        'pointer-events-auto flex max-w-[560px] items-center gap-3 rounded-lg px-4 py-2.5 text-[13px] text-white shadow-lg',
        toast.tone === 'danger' ? 'bg-danger' : 'bg-ink',
      )}
    >
      <span className="min-w-0">{toast.message}</span>
      {toast.action ? (
        <Button
          size="sm"
          variant="ghost"
          className="shrink-0 text-white underline underline-offset-2 hover:bg-white/15 hover:text-white"
          onClick={() => {
            toast.action?.onAction();
            onDismiss();
          }}
        >
          {toast.action.label}
        </Button>
      ) : null}
    </div>
  );
}
